import dynamic from "next/dynamic"
import { useState, useEffect, createContext, useContext, useCallback } from "react"
const Confetti = dynamic(() => import("react-confetti"), { ssr: false })

interface ConfettiOrigin {
    x: number
    y: number
    w: number
    h: number
}

interface ConfettiContextValue {
    setShowConfetti: (show: boolean) => void
}

const ConfettiContext = createContext<ConfettiContextValue | null>(null)
ConfettiContext.displayName = "ConfettiContext"
export const useConfettiContext = () => {
    const context = useContext(ConfettiContext)
    if (!context) throw new Error("useConfettiContext must be used within ConfettiContextProvider")
    return context
}

// Query the Start Growth button position directly from DOM
function getStartButtonPosition(): ConfettiOrigin {
    // Try to find the Start Growth button by data attribute
    const button = document.querySelector('[data-confetti-origin="true"]') as HTMLElement | null
    
    if (button) {
        const rect = button.getBoundingClientRect()
        console.log("[Confetti] Found button at:", rect.left, rect.top, rect.width, rect.height)
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            w: rect.width,
            h: rect.height
        }
    }
    
    // Fallback: Calculate position based on layout
    // Sidebar is ~260px, content area is centered, button is at top-right of content
    const viewportWidth = window.innerWidth
    const sidebarWidth = 260
    const contentMaxWidth = 1152 // max-w-6xl
    const contentWidth = Math.min(viewportWidth - sidebarWidth, contentMaxWidth)
    const contentStart = sidebarWidth + Math.max(0, (viewportWidth - sidebarWidth - contentWidth) / 2)
    const buttonX = contentStart + contentWidth - 100 // 100px from right edge of content
    
    console.log("[Confetti] Using fallback position:", buttonX, 55)
    
    return {
        x: buttonX,
        y: 55, // Header height
        w: 140,
        h: 40
    }
}

export default function ConfettiContextProvider({ children }) {
    const [showConfetti, setShowConfettiState] = useState(false)
    const [confettiSource, setConfettiSource] = useState<ConfettiOrigin | null>(null)
    const [renderConfetti, setRenderConfetti] = useState(false)

    // Wrapper that calculates position when triggered
    const setShowConfetti = useCallback((show: boolean) => {
        if (show && typeof window !== "undefined") {
            const origin = getStartButtonPosition()
            console.log("[Confetti] Setting origin:", origin)
            setConfettiSource(origin)
        }
        setShowConfettiState(show)
    }, [])

    // When showConfetti changes to false, cleanup after animation
    useEffect(() => {
        if (!showConfetti) {
            const timeoutId = setTimeout(() => {
                setRenderConfetti(false)
                setConfettiSource(null)
            }, 1000)
            return () => clearTimeout(timeoutId)
        } else {
            setRenderConfetti(true)
        }
    }, [showConfetti])

    // Get window dimensions for confetti canvas
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
    useEffect(() => {
        const updateSize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight })
        }
        updateSize()
        window.addEventListener("resize", updateSize)
        return () => window.removeEventListener("resize", updateSize)
    }, [])

    return (
        <ConfettiContext.Provider value={{ setShowConfetti }}>
            {renderConfetti && windowSize.width > 0 && confettiSource && (
                <Confetti
                    className={`confetti ${showConfetti ? "" : "fade-out"}`}
                    width={windowSize.width}
                    height={windowSize.height}
                    numberOfPieces={showConfetti ? 250 : 0}
                    confettiSource={confettiSource}
                    initialVelocityY={-30}
                    initialVelocityX={-20}
                    gravity={0.15}
                    wind={-0.01}
                    friction={0.99}
                    colors={["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a", "#f97316"]}
                    recycle={false}
                    tweenDuration={100}
                    style={{ position: "fixed", top: 0, left: 0, zIndex: 9999, pointerEvents: "none" }}
                />
            )}
            {children}
        </ConfettiContext.Provider>
    )
}
